import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import AgentSearch from 'components/agents/agent_search';
import connect from 'components/lib/connect';
import { GRAYS } from 'components/common/colors';
import LoadingSpinner from 'components/lib/loading_spinner';
import Paginate from 'components/common/paginate';
import { Table, TBody, TD, TH, THead, TR } from 'components/common/table';
import UsersTableRow from './users_table_row';

export const Users = styled(Table)`
  border-left: none;
  border-right: none;
  margin-bottom: ${p => p.theme.spacing.stackLarge};
`;

const NoUsersCell = styled(TD)`
  padding-left: ${p => p.theme.spacing.insetLarge};
`;

export const PAGE_ROW_COUNT = 25;

export class UsersTable extends React.Component {
  constructor(props) {
    super(props);
    _.bindAll(this, ['handleChangePage', 'handleUsersTableRowClick', 'renderUsersTableRow']);

    let paginationStartIndex = this.props.paginationStartIndex || 0;
    this.state = {
      agentsForPage: this.getAgentsForPage(this.props.agents, paginationStartIndex),
      allAgents: this.props.agents,
      paginationStartIndex,
      isLoading: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.agents !== this.props.agents) {
      let paginationStartIndex = this.state.paginationStartIndex;
      if (nextProps.agents.length < PAGE_ROW_COUNT || nextProps.textFilter !== this.props.textFilter) {
        paginationStartIndex = 0;
      }
      this.setState({
        agentsForPage: this.getAgentsForPage(nextProps.agents, paginationStartIndex),
        allAgents: nextProps.agents,
        paginationStartIndex,
      });
    }
  }

  getAgentsForPage(allAgents, paginationStartIndex) {
    return allAgents.slice(paginationStartIndex, paginationStartIndex + PAGE_ROW_COUNT);
  }

  render() {
    let { isLoading, canSelectUsers } = this.props;
    if (isLoading) {
      return <LoadingSpinner color={GRAYS.xlight} position="relative" />;
    }

    return (
      <React.Fragment>
        <Users zebra inset="small">
          <THead>
            <TR>
              {canSelectUsers ? <TH /> : null}
              <TH>Name</TH>
              <TH>Teams</TH>
              <TH>Roles</TH>
              {canSelectUsers ? null : <TH>Inboxes</TH>}
              {canSelectUsers ? null : <TH>Status</TH>}
            </TR>
          </THead>
          <TBody>{this.renderUsers()}</TBody>
        </Users>
        {this.state.allAgents.length > PAGE_ROW_COUNT && (
          <Paginate
            from={this.state.paginationStartIndex}
            size={PAGE_ROW_COUNT}
            total={this.state.allAgents.length}
            onChange={this.handleChangePage}
          />
        )}
      </React.Fragment>
    );
  }

  renderUsersTableRow(agent) {
    return (
      <UsersTableRow
        key={agent.id}
        agent={agent}
        canSelectUsers={this.props.canSelectUsers}
        isSelected={_.includes(this.props.selectedUserIds, agent.id)}
        onUserSelectClick={this.handleUsersTableRowClick}
      />
    );
  }

  renderUsers() {
    if (this.state.agentsForPage.length === 0) {
      return (
        <TR>
          <NoUsersCell data-aid="noUsersCell" colSpan={this.props.canSelectUsers ? '4' : '5'}>
            No users to display
          </NoUsersCell>
        </TR>
      );
    }
    return _.map(this.state.agentsForPage, this.renderUsersTableRow);
  }

  handleUsersTableRowClick(userId) {
    this.props.onUserSelectClick({ userId, paginationStartIndex: this.state.paginationStartIndex });
  }

  handleChangePage(paginationStartIndex) {
    let agentsForPage = this.state.allAgents.slice(paginationStartIndex, paginationStartIndex + PAGE_ROW_COUNT);
    this.setState({ agentsForPage, paginationStartIndex });
  }
}

UsersTable.propTypes = {
  isLoading: PropTypes.bool,
  paginationStartIndex: PropTypes.number,
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  canSelectUsers: PropTypes.bool,
  onUserSelectClick: PropTypes.func,
  selectedUserIds: PropTypes.arrayOf(PropTypes.string),
};

export class UsersTableSearcher extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromProps({ agentSearch, agents, textFilter }, state) {
    if (!textFilter) {
      if (agentSearch !== state.agentSearch || textFilter !== state.textFilter) {
        let agentList = _.sortBy(agentSearch.list, agent => agent.name.toLowerCase() || agent.email.toLowerCase());
        return { agentSearch, agents: agentList, textFilter };
      }
    } else if (agentSearch !== state.agentSearch || textFilter !== state.textFilter) {
      agents = agentSearch.search(textFilter);
      return {
        agentSearch,
        agents,
        textFilter,
      };
    }
    return null;
  }

  render() {
    return <UsersTable {..._.omit(this.props, ['agentSearch'])} agents={this.state.agents} />;
  }
}

UsersTableSearcher.propTypes = {
  ...UsersTable.propTypes,
  agentSearch: PropTypes.object,
  textFilter: PropTypes.string,
};

export function UsersTableWithSearch(props) {
  return (
    <AgentSearch allowDisabled>
      {({ agentSearch }) => <UsersTableSearcher {...props} agentSearch={agentSearch} />}
    </AgentSearch>
  );
}

const UsersTableContainer = connect(mapStateToProps)(UsersTableWithSearch);

UsersTableContainer.propTypes = {
  textFilter: PropTypes.string,
  onUserSelectClick: PropTypes.func,
};

function mapStateToProps({ getProvider }) {
  const agentProfilesProvider = getProvider('agentProfiles');
  const usersProvider = getProvider('users');
  const currentLocation = getProvider('currentLocation').get();

  return {
    isLoading: usersProvider.isLoading() || agentProfilesProvider.isLoading(),
    paginationStartIndex: _.get(currentLocation, 'paginationStartIndex'),
  };
}

export default UsersTableContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/users/users_table.jsx